(ns practitest.frontend.common.re-frame
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [clojure.walk :as walk]
            [malli.core :as m]
            [malli.transform :as mt]
            [clojure.string :as str]
            [ajax.core :as ajax]))

(defn build-event-vector
  "Small helper to dynamically build event vectors for re-frame.

  Will accept `event` in form of either `:keyword` or `[:keyword arg]` 
  and will build resulting event vector by concatenating args to base event.

  e.g. `(build-event-vector :keyword 1 2 3) => [:keyword 1 2 3]` and
       `(build-event-vector [:keyword 1] 2 3) => [:keyword 1 2 3]`

  Used in dynamic and conditional dispatch functions"
  [event & args]
  (if (coll? event)
    (apply conj event args)
    (apply conj [event] args)))

;; Stable DOM handlers
(defn dispatch2
  [event & params]
  (re-frame/dispatch (apply build-event-vector event params)))

(defn partial-dispatch
  [event]
  (reagent/partial dispatch2 event))

(defn dispatch2-sync
  [event & params]
  (re-frame/dispatch-sync (apply build-event-vector event params)))

(defn partial-dispatch-sync
  [event]
  (reagent/partial dispatch2-sync event))

(defn subscribe2
  [query & params]
  (re-frame/subscribe (apply build-event-vector query params)))

(defn partial-subscribe
  [event]
  (reagent/partial subscribe2 event))

(defn conditional-dispatch
  "Will try to append event with args to effects map inside `:fx` section using `:dispatch` effect.

  * Will preserve existing `:fx` items
  * Will return effects-map unchanged if event is `nil`"
  [effects-map event & args]
  (if (some? event)
    (update effects-map :fx
            (fn [fx]
              (let [event-vector (apply build-event-vector event args)]
                (if (coll? fx)
                  (conj fx [:dispatch event-vector])
                  [[:dispatch event-vector]]))))
    effects-map))

(comment
  (conditional-dispatch {} [:foo 1] 2 3)
  (conditional-dispatch {:dispatch []} [:foo 1] 2 3)
  (conditional-dispatch {:fx [[:dispatch [:asd]]]} :foo 2 3)
  (conditional-dispatch {:fx [[:dispatch [:asd]]]} nil 2 3))

(defn underscore->dash [m]
  (walk/postwalk
   (fn [x]
     (if (map? x)
       (update-keys x #(-> %
                           name
                           (str/replace "_" "-")
                           keyword))
       x))
   m))

(defn reg-load-data-event
  "Registers three re-frame events from a single config map.

   Params:
   :evt       — keyword, event id (also used to derive -success/-failed ids)
   :db-path   — vector or fn [entity], path in app-db to store the result
   :url-for   — fn [db & args], computes URL from db and event args;
                if omitted, event expects a URL as its first argument
   :schema    — malli schema, validates the transform output
   :transform — fn [evt data], transforms response before storing (default: identity)
   :merge-by  — keyword, if set stores result under (get data merge-by) key at db-path;
                otherwise merges result into existing map at db-path

   Registered events:
   event-fx:
     evt — dispatched as [evt url] or [evt & args] when :url-for is provided

   event-db:
     evt-success — converts underscored keys to dashed, runs :transform,
                   validates result against :schema, merges into :db-path
                   (or stores at db-path[merge-by-key] when :merge-by is set);
                   logs error if validation fails
     evt-failed  — logs error, leaves db unchanged"
  [{:keys [evt db-path url-for merge-by schema transform]
    :or   {transform identity
           schema    :any}}]
  (let [ns             (namespace evt)
        success-evt    (keyword ns (str (name evt) "-success"))
        failed-evt     (keyword ns (str (name evt) "-failed"))
        mschema        (m/schema schema)
        coerce-data    (m/coercer mschema (mt/transformer mt/string-transformer mt/default-value-transformer))
        success-handler (fn [db [_ arg response]]
                          (let [path    (if (fn? db-path) (db-path arg) db-path)
                                raw     (->> response underscore->dash (transform evt))
                                data    (try
                                          (coerce-data raw)
                                          (catch :default e
                                            (js/console.error "Coercion failed for" evt (ex-message e))
                                            raw))]
                            (if merge-by
                              (let [k (get data merge-by)]
                                (update-in db path merge {k data}))
                              (update-in db path merge data))))
        failed-handler  (fn [db [_ arg response]]
                          (when ^boolean goog.DEBUG
                            (js/console.error "Load data: failed" evt response))
                          db)]
    (re-frame/reg-event-fx
     evt
     (fn [{:keys [db]} [_ arg]]
       (let [url (if (fn? url-for) (url-for db arg) arg)]
         {:http-xhrio {:method          :get
                       :format          (ajax/json-request-format)
                       :response-format (ajax/json-response-format)
                       :uri             url
                       :on-success      [success-evt arg]
                       :on-failure      [failed-evt arg]}})))

    (re-frame/reg-event-db
     success-evt
     success-handler)

    (re-frame/reg-event-db
     failed-evt
     failed-handler)))

(defn reg-load-data-events [& events]
  (doseq [evt events]
    (reg-load-data-event evt)))
